import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, Settings } from 'lucide-react';
import { Button } from "@/components/ui/button";

const COOKIE_CONSENT_KEY = 'cookie_consent_status';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to avoid flash
      setTimeout(() => setShowBanner(true), 500);
    } else {
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (e) {
        setShowBanner(true);
      }
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setShowBanner(false);
    setShowSettings(false);
    
    // Trigger consent event for analytics tools
    window.dispatchEvent(new CustomEvent('cookieConsentUpdate', { detail: prefs }));
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
      timestamp: new Date().toISOString()
    };
    saveConsent(allAccepted);
  };

  const acceptSelected = () => {
    saveConsent({
      ...preferences,
      timestamp: new Date().toISOString()
    });
  };

  const rejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
      timestamp: new Date().toISOString()
    };
    saveConsent(onlyNecessary);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" onClick={() => {}} />
      
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6" data-testid="cookie-banner">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          {!showSettings ? (
            /* Main Banner */
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="bg-amber-100 p-3 rounded-full">
                  <Cookie className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    🍪 Utilizziamo i Cookie
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Questo sito utilizza cookie tecnici necessari al funzionamento e cookie di terze parti 
                    per analisi e marketing. Puoi accettare tutti i cookie, personalizzare le tue preferenze 
                    o rifiutare quelli non necessari. Per maggiori informazioni consulta la nostra{' '}
                    <a href="/cookie-policy" className="text-amber-600 hover:underline">Cookie Policy</a> e{' '}
                    <a href="/privacy-policy" className="text-amber-600 hover:underline">Privacy Policy</a>.
                  </p>
                  
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      onClick={acceptAll}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid="cookie-accept-all"
                    >
                      Accetta Tutti
                    </Button>
                    <Button 
                      onClick={rejectAll}
                      variant="outline"
                      className="border-slate-300"
                      data-testid="cookie-reject-all"
                    >
                      Rifiuta Non Necessari
                    </Button>
                    <Button 
                      onClick={() => setShowSettings(true)}
                      variant="ghost"
                      className="text-slate-600"
                      data-testid="cookie-settings"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Personalizza
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Settings Panel */
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-600" />
                  Preferenze Cookie
                </h3>
                <button 
                  onClick={() => setShowSettings(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-start justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900">Cookie Necessari</h4>
                    <p className="text-sm text-slate-500 mt-1">
                      Essenziali per il funzionamento del sito. Non possono essere disattivati.
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="w-12 h-6 bg-amber-500 rounded-full flex items-center justify-end px-1">
                      <div className="w-4 h-4 bg-white rounded-full" />
                    </div>
                  </div>
                </div>
                
                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900">Cookie Analitici</h4>
                    <p className="text-sm text-slate-500 mt-1">
                      Ci aiutano a capire come i visitatori interagiscono con il sito.
                    </p>
                  </div>
                  <button 
                    onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                    className="ml-4"
                  >
                    <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.analytics ? 'bg-amber-500 justify-end' : 'bg-slate-300 justify-start'
                    }`}>
                      <div className="w-4 h-4 bg-white rounded-full" />
                    </div>
                  </button>
                </div>
                
                {/* Marketing Cookies */}
                <div className="flex items-start justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900">Cookie di Marketing</h4>
                    <p className="text-sm text-slate-500 mt-1">
                      Utilizzati per mostrare annunci pertinenti ai tuoi interessi.
                    </p>
                  </div>
                  <button 
                    onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                    className="ml-4"
                  >
                    <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.marketing ? 'bg-amber-500 justify-end' : 'bg-slate-300 justify-start'
                    }`}>
                      <div className="w-4 h-4 bg-white rounded-full" />
                    </div>
                  </button>
                </div>
                
                {/* Preference Cookies */}
                <div className="flex items-start justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900">Cookie di Preferenze</h4>
                    <p className="text-sm text-slate-500 mt-1">
                      Permettono al sito di ricordare le tue scelte (lingua, regione).
                    </p>
                  </div>
                  <button 
                    onClick={() => setPreferences(p => ({ ...p, preferences: !p.preferences }))}
                    className="ml-4"
                  >
                    <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.preferences ? 'bg-amber-500 justify-end' : 'bg-slate-300 justify-start'
                    }`}>
                      <div className="w-4 h-4 bg-white rounded-full" />
                    </div>
                  </button>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  onClick={acceptSelected}
                  className="bg-amber-500 hover:bg-amber-600 text-white flex-1"
                >
                  Salva Preferenze
                </Button>
                <Button 
                  onClick={acceptAll}
                  variant="outline"
                  className="flex-1"
                >
                  Accetta Tutti
                </Button>
              </div>
            </div>
          )}
          
          {/* GDPR Footer */}
          <div className="bg-slate-50 px-6 py-3 border-t border-slate-200">
            <p className="text-xs text-slate-500 text-center">
              Conforme al GDPR (Regolamento UE 2016/679) e alla Direttiva ePrivacy. 
              I tuoi dati sono al sicuro.{' '}
              <a href="/privacy-policy" className="text-amber-600 hover:underline">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
